import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
    EnvironmentProviders,
    InjectionToken,
    Provider,
    makeEnvironmentProviders,
    provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NavigationActionTiming, provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { of } from 'rxjs';

import { defaultHttpInterceptorOptions } from '@mobi/b2e-utils-onprem-ng-jslib';
import {
    provideAppAuthentication,
    withAuthenticatedUserTelemetry,
    withAutoLogin,
    withCipErrorHandler,
    withImpersonation,
} from '@mobi/rwc-utils-authentication-jslib';
import { provideAzureCore } from '@mobi/rwc-utils-azure-ng-jslib';
import { LANGUAGE_PROVIDER, Language, LanguageProvider, SPAConfiguration } from '@mobi/rwc-utils-ng-jslib';
import { configureDefaultInterceptors } from '@mobi/rwc-utils-onprem-ng-jslib';
import { FaviconService } from '@mobi/rwc-uxframework-ng';
import { TealiumConfigLoader, TealiumConfigStaticLoader, provideTealiumTracking } from '@mobi/tea-tagmanager-ng-jslib';

import { type LocalBrand } from '../common.constant';
import { provideOivCoreSharedStore } from '../store/oiv-core-shared.provider';

import { APP_SETTING_PROVIDER, MobiliarB2cSettingProvider, MobiliarB2eSettingProvider } from './setting';

// This is a Strategy Pattern for LanguageProvider
class LocalStorageLanguageProvider implements LanguageProvider {
    getLanguage() {
        return of(localStorage.getItem('settings.language') as Language);
    }
}

export const WINDOW = new InjectionToken<Window>('WINDOW');

// Override this token in a local app; hostname detection remains the default.
export const LOCAL_BRAND = new InjectionToken<LocalBrand>('LOCAL_BRAND', {
    providedIn: 'root',
    factory: () => undefined,
});

let rwcProviders: EnvironmentProviders | (EnvironmentProviders | Provider)[];
if ((window.document.defaultView as Window).MOBI_RWC_CONFIG?.isCloudApp === 'true') {
    rwcProviders = [
        provideAzureCore(),
        provideAppAuthentication(
            withAuthenticatedUserTelemetry(),
            withAutoLogin(),
            withCipErrorHandler(),
            withImpersonation(),
        ),
    ] as EnvironmentProviders[];
} else {
    rwcProviders = configureDefaultInterceptors(defaultHttpInterceptorOptions);
}

export function provideB2eOivCore(): EnvironmentProviders {
    return makeEnvironmentProviders([
        provideAnimationsAsync(),
        provideHttpClient(withInterceptorsFromDi()),
        provideZoneChangeDetection({
            eventCoalescing: true,
            runCoalescing: true,
        }),
        rwcProviders,
        provideStore(),
        provideRouterStore({
            navigationActionTiming: NavigationActionTiming.PostActivation,
        }),
        provideStoreDevtools(),
        provideTealiumTracking({
            configLoader: {
                provide: TealiumConfigLoader,
                useFactory: tealiumConfigLoaderFactory,
            },
        }),
        {
            provide: LANGUAGE_PROVIDER,
            useClass: LocalStorageLanguageProvider,
        },
        provideOivCoreSharedStore,
        FaviconService,
        {
            provide: APP_SETTING_PROVIDER,
            useClass: MobiliarB2eSettingProvider,
            deps: [FaviconService],
        },
        { provide: WINDOW, useValue: window },
    ]);
}

export function provideB2cOivCore(): EnvironmentProviders {
    return makeEnvironmentProviders([
        provideAnimationsAsync(),
        provideHttpClient(withInterceptorsFromDi()),
        provideZoneChangeDetection({
            eventCoalescing: true,
            runCoalescing: true,
        }),
        provideAzureCore(),
        provideStore(),
        provideRouterStore({
            navigationActionTiming: NavigationActionTiming.PostActivation,
        }),
        provideStoreDevtools(),
        provideTealiumTracking({
            configLoader: {
                provide: TealiumConfigLoader,
                useFactory: tealiumConfigLoaderFactory,
            },
        }),
        {
            provide: LANGUAGE_PROVIDER,
            useClass: LocalStorageLanguageProvider,
        },
        provideOivCoreSharedStore,
        FaviconService,
        {
            provide: APP_SETTING_PROVIDER,
            useClass: MobiliarB2cSettingProvider,
            deps: [FaviconService],
        },
        { provide: WINDOW, useValue: window },
    ]);
}

function tealiumConfigLoaderFactory(): TealiumConfigLoader {
    const RWC_CONFIG: SPAConfiguration = (window.document.defaultView as Window).MOBI_RWC_CONFIG;
    return new TealiumConfigStaticLoader({
        account: 'mobiliar',
        profile: 'oiv',
        environment: RWC_CONFIG.stage === 'prod' ? 'prod' : 'dev',
    });
}
